// CourseShell — sidebar + main layout for /course/:id sub-routes.
//
// Decision history:
//   Session 19G (§5.3): top tab bar.
//   Session 19H (§H1): replaced with v1-style left sidebar — 陛下 explicitly
//     asked for v1 sidebar polished + extended. The sidebar carries course
//     identity (title, countdown), nav (filtered by enabledComponents),
//     course actions (regenerate, paste-JSON), and footer (lang toggle +
//     mini brand). Top global header is NOT rendered on /course routes —
//     MainLayout no longer wraps them; the sidebar self-contains everything.
//   Session 19AN: stale banner shown above content when the IngestPackage's
//     sourceHash (computeSourceHash · source-hash.ts) no longer matches the
//     current CourseDraft. This is an independent isStale boolean fed by
//     course.tsx — NOT the deleted §3 八态机 (deriveOverallState removed
//     2026-06-30 · DEBT-19BO+-OVERALL-STATE-DEAD-CODE).

import { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { AlertTriangle, RotateCw, Menu } from 'lucide-react';
import { CourseSidebar } from './course-sidebar.js';
// Isomorphism Phase 3: Zustand primary (replacing Context).
import { useStoreCourseData } from '../lib/hooks/use-store-course.js';
import { useStore } from '../lib/store/index.js';
import { pollIngestStatus } from '../api/ingest.js';
import type { StatusResult } from '@examforge/shared/api';
import { stageLabelKey } from '../lib/regen-utils.js';
import { useT } from '../i18n/index.js';
import { useEventBus } from '../lib/hooks/use-event-bus.js';
import { useNotify } from '../lib/hooks/use-notify.js';
// B5 桥5: 后台自动诊断调度器（opt-in · 见 diagnose-slice.autoDiagnoseEnabled）。
import { useAutoDiagnose } from '../lib/hooks/use-auto-diagnose.js';

/**
 * Extract regen items from a validated StatusResult.
 * Phase 5b: TypeScript discriminated union narrowing replaces the old
 * manual `in` checks + `as` casts. Each branch is type-safe.
 * Returns null if the result is a full ingest (no regen items).
 */
function extractRegenItems(result: StatusResult): unknown[] | null {
  if ('v4Regen' in result) return result.proposedItems;
  if ('partialRegen' in result) return result.items;
  return null; // full IngestPackage — no regen items
}

export function CourseShell() {
  const { isStale, regenerate, draft } = useStoreCourseData();
  const t = useT();
  // B5 桥5: 启动后台自动诊断调度器。opt-in（默认关）；产出的 verdict 进
  // progress.diagnosisPending 待审队列，用户 accept 才写 diagnosis。
  useAutoDiagnose();
  // 19AT · S9: mobile sidebar drawer. Closed by default; toggle button
  // visible only at < 720px (CSS-driven). Auto-close on route change so the
  // user isn't stuck looking at the overlay after picking a section.
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- auto-close mobile sidebar drawer on route change so the overlay doesn't linger; key-reset alternative needs router to own pathname-driven remount
    setSidebarOpen(false);
  }, [pathname]);

  // ── Global regen poll — keeps running even when RefineView unmounts ──────
  const regenStatus = useStore((s) => s.regenStatus);
  const regenRunId = useStore((s) => s.regenRunId);
  const regenTargetStage = useStore((s) => s.regenTargetStage);
  const regenTargetChapter = useStore((s) => s.regenTargetChapter);
  const regenMode = useStore((s) => s.regenMode);
  const setRegenState = useStore((s) => s.setRegenState);
  const restoreRegenFromSession = useStore((s) => s.restoreRegenFromSession);
  const pollActiveRef = useRef(false);
  const pollStartedAtRef = useRef<number>(0);
  const eventBus = useEventBus();
  const notify = useNotify();

  // ── Restore regen state from sessionStorage after page refresh ──────────
  // Also detects interrupted sync-path regens (no runId) and notifies user.
  useEffect(() => {
    // Peek at sessionStorage BEFORE restore to detect interrupted sync regens.
    // restoreRegenFromSession will clear it, so we must read first.
    let interruptedStage = '';
    try {
      const raw = sessionStorage.getItem('examforge_regen_state');
      if (raw) {
        const saved = JSON.parse(raw) as Record<string, unknown>;
        const status = saved['regenStatus'] as string | undefined;
        const runId = saved['regenRunId'] as string | null | undefined;
        if (status && !runId && (status === 'preparing' || status === 'starting')) {
          interruptedStage = (saved['regenTargetStage'] as string) ?? '';
        }
      }
    } catch { /* corrupt entry, ignore */ }

    restoreRegenFromSession();

    // If a sync-path regen was interrupted, notify the user.
    if (interruptedStage) {
      const stageLabel = t(stageLabelKey(interruptedStage));
      notify({
        level: 'warning',
        source: 'regen',
        title: t('regen.notifInterrupted', { stage: stageLabel }),
        persistent: true,
      });
    }
  }, [restoreRegenFromSession, notify, t]);

  // ── Regen notification helpers ────────────────────────────────────────────
  // useCallback so they can be named in the poll effect's dep array without
  // retriggering it on every render (deps match what's already in the effect's
  // array). (react-hooks/exhaustive-deps · Part E 第四波)
  const emitRegenCompleted = useCallback((itemCount: number, durationMs: number) => {
    const stageLabel = t(stageLabelKey(regenTargetStage));
    const duration = `${Math.round(durationMs / 1000)}s`;
    eventBus.emit('regen:completed', {
      courseId: draft.id,
      stage: regenTargetStage,
      itemCount,
      durationMs,
    });
    notify({
      level: 'success',
      source: 'regen',
      title: t('regen.notifCompleted', { stage: stageLabel, count: itemCount, duration }),
      persistent: true,
      action: {
        label: t('regen.notifView'),
        to: `/course/${draft.id}/regen`,
      },
    });
  }, [t, regenTargetStage, draft.id, eventBus, notify]);

  const emitRegenFailed = useCallback((error: string) => {
    const stageLabel = t(stageLabelKey(regenTargetStage));
    eventBus.emit('regen:failed', {
      courseId: draft.id,
      stage: regenTargetStage,
      error,
    });
    notify({
      level: 'error',
      source: 'regen',
      title: t('regen.notifFailed', { stage: stageLabel }),
      message: error,
      persistent: true,
    });
  }, [t, regenTargetStage, draft.id, eventBus, notify]);

  useEffect(() => {
    if (regenStatus !== 'running' || !regenRunId) return;
    if (pollActiveRef.current) return; // prevent duplicate polls
    pollActiveRef.current = true;
    pollStartedAtRef.current = Date.now();

    // Emit regen:started → notification system picks it up
    const stageLabel = t(stageLabelKey(regenTargetStage));
    eventBus.emit('regen:started', {
      courseId: draft.id,
      stage: regenTargetStage,
      chapter: regenTargetChapter ?? undefined,
      mode: regenMode,
    });
    notify({
      level: 'info',
      source: 'regen',
      title: t('regen.notifStarted', { stage: stageLabel }),
      persistent: true,
    });

    let active = true;
    let timeout: ReturnType<typeof setTimeout>;

    const poll = async () => {
      try {
        const res = await pollIngestStatus(regenRunId);
        if (!active) return;

        if (res.status === 'completed') {
          try {
            const resultPkg = res.package;
            if (resultPkg) {
              // Phase 5b: StatusResult is a validated discriminated union.
              // TypeScript narrows automatically via `in` checks.
              const regenItems = extractRegenItems(resultPkg);
              if (regenItems && regenItems.length > 0) {
                // v4 shape: extract typed fields via narrowing
                const regenId = 'v4Regen' in resultPkg ? resultPkg.regenId : null;
                const beforeSnapshot = 'v4Regen' in resultPkg ? resultPkg.beforeSnapshot : null;
                const diffSummary = 'v4Regen' in resultPkg ? JSON.stringify(resultPkg.diffSummary) : null;
                setRegenState({
                  regenStatus: 'confirm',
                  regenProgress: '',
                  regenPendingItems: regenItems,
                  regenId,
                  regenBeforeSnapshot: beforeSnapshot,
                  regenDiffSummary: diffSummary,
                });
                // Emit completion notification
                emitRegenCompleted(regenItems.length, res.durationMs);
                pollActiveRef.current = false;
                return;
              }
              // v4 regen with empty proposedItems (e.g. delete-only action):
              // still treat as done — items were already removed by the workflow.
              if ('v4Regen' in resultPkg && resultPkg.proposedItems.length === 0) {
                setRegenState({
                  regenStatus: 'done',
                  regenProgress: t('refine.done', { tokens: res.inputTokens + res.outputTokens, duration: `${Math.round(res.durationMs / 1000)}s` }),
                });
                emitRegenCompleted(0, res.durationMs);
                pollActiveRef.current = false;
                return;
              }
            }
          } catch (e) {
            // 19BM-C: regen result parse error — best-effort path. The resultPkg
            // was already validated by the backend's StatusResultSchema.parse(),
            // so we fall through to 'done'. Console guarded for prod silence.
            // eslint-disable-next-line no-console -- DEV-only diagnostic, stripped by Vite in prod build
            if (import.meta.env.DEV) console.error('[course-shell] regen result parse error:', e);
          }
          setRegenState({
            regenStatus: 'done',
            regenProgress: t('refine.done', { tokens: res.inputTokens + res.outputTokens, duration: `${Math.round(res.durationMs / 1000)}s` }),
          });
          emitRegenCompleted(0, res.durationMs);
          pollActiveRef.current = false;
          return;
        }

        if (res.status === 'failed') {
          setRegenState({
            regenStatus: 'failed',
            regenProgress: t('refine.failed', { error: res.errorMessage ?? 'unknown' }),
          });
          emitRegenFailed(res.errorMessage ?? 'unknown');
          pollActiveRef.current = false;
          return;
        }

        // Still running — update progress
        setRegenState({
          regenProgress: t('refine.running', { stage: t(stageLabelKey(res.lastStage ?? regenTargetStage)), done: res.stagesDone }),
        });
        timeout = setTimeout(poll, 3000);
      } catch (pollErr) {
        // eslint-disable-next-line no-console -- DEV-only diagnostic, stripped by Vite in prod build
        if (import.meta.env.DEV) console.error('[course-shell] regen poll error:', pollErr);
        if (active) {
          timeout = setTimeout(poll, 5000);
        }
      }
    };

    poll();
    return () => {
      active = false;
      clearTimeout(timeout);
      pollActiveRef.current = false;
    };
  }, [regenStatus, regenRunId, regenTargetStage, regenTargetChapter, regenMode, setRegenState, t, eventBus, notify, draft.id, emitRegenCompleted, emitRegenFailed]);

  return (
    <div className="course-layout">
      <button
        type="button"
        className="course-mobile-toggle"
        onClick={() => setSidebarOpen((v) => !v)}
        aria-label={sidebarOpen ? t('nav.sidebarToggleClose') : t('nav.sidebarToggleOpen')}
        aria-expanded={sidebarOpen}
        aria-controls="course-sidebar"
      >
        <Menu size={18} aria-hidden="true" />
      </button>
      <div
        id="course-sidebar"
        className={`course-sidebar-mobile-host${sidebarOpen ? ' is-open' : ''}`}
      >
        <CourseSidebar />
      </div>
      <div
        className="course-mobile-overlay"
        onClick={() => setSidebarOpen(false)}
        aria-hidden="true"
      />
      <div className="course-content">
        {isStale && (
          <div className="course-stale-banner" role="alert">
            <AlertTriangle size={16} aria-hidden="true" />
            <span style={{ flex: 1 }}>{t('course.staleBanner')}</span>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => { void regenerate(); }}
            >
              <RotateCw size={13} aria-hidden="true" />
              {t('course.staleRegenerate')}
            </button>
          </div>
        )}
        <Outlet />
      </div>
    </div>
  );
}
